/**
 * Camera capture: face tracking, region sampling, and frame quality gating.
 *
 * Three jobs, in order of how much they matter:
 *
 *   1. Find the face. A fixed rectangle in the middle of the frame works in a
 *      demo and fails the moment someone sits slightly off-centre, which is
 *      most people most of the time.
 *   2. Sample forehead and both cheeks separately, in RGB. The engine fuses
 *      them and cross-checks them against each other; it can only do that if
 *      they arrive separately.
 *   3. Decide, per frame, whether the frame is usable at all — and say so,
 *      rather than quietly feeding motion and clipping into the estimator.
 *
 * Everything stays on the device. No frame is uploaded, ever, and none is
 * retained beyond the few milliseconds it takes to average three small boxes.
 */

export const CAPTURE_FPS = 30;

/** Working resolution. Averaging boxes does not need pixels. */
const WORK_W = 96;
const WORK_H = 72;

/**
 * Region boxes as fractions of the detected face box.
 * Forehead sits above the brow line; cheeks avoid the nose and the mouth.
 */
export const REGION_LAYOUT = Object.freeze([
  { name: 'forehead', x: 0.30, y: 0.08, w: 0.40, h: 0.18 },
  { name: 'left cheek', x: 0.12, y: 0.44, w: 0.24, h: 0.22 },
  { name: 'right cheek', x: 0.64, y: 0.44, w: 0.24, h: 0.22 },
]);

/** Fallback face box when no detector is available — centred, portrait-ish. */
const DEFAULT_FACE = Object.freeze({ x: 0.28, y: 0.14, w: 0.44, h: 0.66 });

export class CameraSource {
  constructor() {
    this.stream = null;
    this.video = null;
    this.canvas = null;
    this.ctx = null;
    this.running = false;

    this.detector = null;
    this.detectorFailed = false;
    this.lastDetect = 0;
    this.face = { ...DEFAULT_FACE };
    this.faceConfident = false;

    this.previousLuma = null;
    this.motion = 0;
  }

  get active() {
    return this.running;
  }

  async start({ videoElement } = {}) {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('This browser will not give a web page camera access.');
    }

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: CAPTURE_FPS },
        facingMode: 'user',
      },
      audio: false,
    });

    this.video = videoElement ?? document.createElement('video');
    this.video.srcObject = this.stream;
    this.video.playsInline = true;
    this.video.muted = true;
    await this.video.play();

    this.canvas = document.createElement('canvas');
    this.canvas.width = WORK_W;
    this.canvas.height = WORK_H;
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });

    // Face detection where the platform offers it. Chrome and some Android
    // browsers ship FaceDetector; Safari and Firefox do not. Its absence is
    // not an error — the centre fallback works, it is simply less forgiving of
    // where the person sits.
    if ('FaceDetector' in window) {
      try {
        this.detector = new window.FaceDetector({ fastMode: true, maxDetectedFaces: 1 });
      } catch {
        this.detector = null;
      }
    }

    this.running = true;
    return this;
  }

  get hasFaceTracking() {
    return Boolean(this.detector) && !this.detectorFailed;
  }

  /** Re-locate the face. Cheap enough at ~4 Hz; pointless every frame. */
  async detectFace() {
    if (!this.detector || this.detectorFailed || !this.video) return;
    const now = performance.now();
    if (now - this.lastDetect < 250) return;
    this.lastDetect = now;

    try {
      const faces = await this.detector.detect(this.video);
      if (!faces.length) {
        this.faceConfident = false;
        return;
      }
      const box = faces[0].boundingBox;
      const vw = this.video.videoWidth || 640;
      const vh = this.video.videoHeight || 480;
      const target = {
        x: box.x / vw,
        y: box.y / vh,
        w: box.width / vw,
        h: box.height / vh,
      };
      // Smooth the box. A jittery region of interest injects exactly the
      // artefact the chrominance layer is there to remove.
      const a = 0.25;
      this.face = {
        x: this.face.x * (1 - a) + target.x * a,
        y: this.face.y * (1 - a) + target.y * a,
        w: this.face.w * (1 - a) + target.w * a,
        h: this.face.h * (1 - a) + target.h * a,
      };
      this.faceConfident = true;
    } catch {
      // Some platforms expose the constructor and then throw on use.
      this.detectorFailed = true;
      this.detector = null;
    }
  }

  /** Region boxes in frame coordinates, for both sampling and the overlay. */
  regionBoxes() {
    const f = this.face;
    return REGION_LAYOUT.map((r) => ({
      name: r.name,
      x: f.x + r.x * f.w,
      y: f.y + r.y * f.h,
      w: r.w * f.w,
      h: r.h * f.h,
    }));
  }

  /**
   * Sample one frame.
   *
   * @returns {{frame: Array<{r,g,b}>, quality: object}|null}
   */
  sample() {
    if (!this.running || !this.video || this.video.readyState < 2) return null;
    this.ctx.drawImage(this.video, 0, 0, WORK_W, WORK_H);

    const boxes = this.regionBoxes();
    const frame = [];
    let totalLuma = 0;
    let clipped = 0;
    let counted = 0;

    for (const box of boxes) {
      const x = clampInt(box.x * WORK_W, 0, WORK_W - 2);
      const y = clampInt(box.y * WORK_H, 0, WORK_H - 2);
      const w = clampInt(box.w * WORK_W, 2, WORK_W - x);
      const h = clampInt(box.h * WORK_H, 2, WORK_H - y);

      const data = this.ctx.getImageData(x, y, w, h).data;
      let r = 0;
      let g = 0;
      let b = 0;
      const pixels = data.length / 4;
      for (let i = 0; i < data.length; i += 4) {
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        // Count saturated pixels: a clipped channel carries no pulse at all,
        // and averaging it in silently flattens the signal.
        if (data[i + 1] >= 253 || data[i + 1] <= 2) clipped++;
        counted++;
      }
      const mean = { r: r / pixels, g: g / pixels, b: b / pixels };
      totalLuma += 0.2126 * mean.r + 0.7152 * mean.g + 0.0722 * mean.b;
      frame.push(mean);
    }

    const luminance = totalLuma / frame.length;
    this.motion = this.estimateMotion();

    return {
      frame,
      quality: this.gradeFrame({
        luminance,
        clippedFraction: counted ? clipped / counted : 0,
        motion: this.motion,
      }),
    };
  }

  /**
   * Frame-to-frame motion, from a coarse luma grid.
   *
   * Deliberately measured on the whole frame rather than the face box: if the
   * head moves, the box follows it and the region content barely changes, so
   * looking only inside the box would under-report exactly the movement we care
   * about.
   */
  estimateMotion() {
    const data = this.ctx.getImageData(0, 0, WORK_W, WORK_H).data;
    const step = 4;
    const grid = [];
    for (let y = 0; y < WORK_H; y += step) {
      for (let x = 0; x < WORK_W; x += step) {
        const i = (y * WORK_W + x) * 4;
        grid.push(0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]);
      }
    }
    if (!this.previousLuma || this.previousLuma.length !== grid.length) {
      this.previousLuma = grid;
      return 0;
    }
    let diff = 0;
    for (let i = 0; i < grid.length; i++) diff += Math.abs(grid[i] - this.previousLuma[i]);
    this.previousLuma = grid;
    return diff / grid.length / 255;
  }

  /**
   * Decide whether a frame is usable, and if not, what the person should do.
   *
   * Issues carry a `code`, never a sentence. The wording lives in the locale
   * files, so guidance a person has to act on within a second arrives in their
   * own language — the one place in this app where a translation is not a
   * courtesy but a functional requirement.
   */
  gradeFrame({ luminance, clippedFraction, motion }) {
    const issues = [];
    let accepted = true;

    if (this.detector && !this.faceConfident) {
      issues.push({ code: 'no_face', severity: 'bad' });
      accepted = false;
    }
    if (luminance < 35) {
      issues.push({ code: 'too_dark', severity: 'bad' });
      accepted = false;
    } else if (luminance < 65) {
      issues.push({ code: 'dim', severity: 'warn' });
    }
    if (clippedFraction > 0.05) {
      issues.push({ code: 'clipped', severity: 'bad' });
      accepted = false;
    } else if (luminance > 225) {
      issues.push({ code: 'bright', severity: 'warn' });
    }
    if (motion > 0.035) {
      issues.push({ code: 'motion', severity: 'bad' });
      accepted = false;
    } else if (motion > 0.018) {
      issues.push({ code: 'slight_motion', severity: 'warn' });
    }

    return {
      accepted,
      luminance,
      clippedFraction,
      motion,
      faceTracked: this.hasFaceTracking && this.faceConfident,
      issues,
      /** The single most useful thing to tell the person right now. */
      advice:
        issues.find((i) => i.severity === 'bad') ??
        issues.find((i) => i.severity === 'warn') ??
        { code: 'ok', severity: 'ok' },
    };
  }

  stop() {
    this.running = false;
    try {
      this.stream?.getTracks().forEach((t) => t.stop());
    } catch {
      /* already gone */
    }
    if (this.video) this.video.srcObject = null;
    this.stream = null;
    this.previousLuma = null;
  }
}

function clampInt(v, min, max) {
  return Math.max(min, Math.min(max, Math.round(v)));
}

/** Human-readable reason a camera attempt failed, shown to the person verbatim. */
export function describeCameraError(err) {
  switch (err?.name) {
    case 'NotAllowedError':
      return 'Camera permission was declined. Ori cannot read a pulse without it — you can still try the simulated subject.';
    case 'NotFoundError':
      return 'No camera was found on this device.';
    case 'NotReadableError':
      return 'The camera is in use by another application.';
    case 'OverconstrainedError':
      return 'This camera cannot provide a usable video mode.';
    case 'SecurityError':
      return 'Camera access needs a secure connection (https).';
    default:
      return err?.message || 'The camera could not be started.';
  }
}

/** The three regions, by the names the overlay and the diagnostics both use. */
export const REGION_NAMES = Object.freeze(REGION_LAYOUT.map((r) => r.name));
